/**
 * User Assignments Management Component
 * Allows org_admin to assign members and programs to users for data-level permissions
 *
 * Updated to use organization roles instead of hardcoded role values.
 * Organization roles are configured per-org and map to system roles for permissions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { User, Users, FolderKanban, Plus, X, AlertCircle, Check, Shield, Star, UserCheck } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAssignableRoles } from '@/hooks/useAssignableRoles';
import type { AssignableRole, UserProgramAssignment } from '@/types/roleTerminology';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface Member {
  id: number;
  name: string;
  email: string;
}

interface Program {
  id: number;
  name: string;
  description: string;
}

interface MemberAssignment {
  id: number;
  member_id: number;
  member_name: string;
  access_level: string;
  assigned_at: string;
}

interface ProgramAssignment {
  id: number;
  program_id: number;
  program_name: string;
  program_role?: string;  // Legacy field
  organization_role_id?: number;
  organization_role?: {
    id: number;
    role_type: string;
    display_name: string;
    system_role: string;
    icon?: string;
    color?: string;
    is_field_worker: boolean;
  };
  is_program_lead: boolean;
  can_edit_program: boolean;
  can_enroll_members: boolean;
  can_manage_members: boolean;
  assigned_at: string;
}

interface UserAssignmentsProps {
  userId: number;
  userName: string;
  userRole: string;
  organizationId: number;
  onClose?: () => void;
}

export function UserAssignments({
  userId,
  userName,
  userRole,
  organizationId,
  onClose
}: UserAssignmentsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [memberAssignments, setMemberAssignments] = useState<MemberAssignment[]>([]);
  const [programAssignments, setProgramAssignments] = useState<ProgramAssignment[]>([]);
  // TRUS-732: assignment id -> on that program's case manager list.
  const [caseManagerFlags, setCaseManagerFlags] = useState<Record<number, boolean>>({});
  const [availableMembers, setAvailableMembers] = useState<Member[]>([]);
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState<string | null>(null);

  // Assignment UI state
  const [showMemberSelector, setShowMemberSelector] = useState(false);
  const [showProgramSelector, setShowProgramSelector] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [memberAccessLevel, setMemberAccessLevel] = useState<string>('full');

  // New organization role-based assignment state
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isProgramLead, setIsProgramLead] = useState(false);
  const [canEnrollMembers, setCanEnrollMembers] = useState(false);
  // Grants edit on program members (enforced by DataPermissions — TRUS-283).
  const [canManageMembers, setCanManageMembers] = useState(true);

  // Fetch assignable organization roles
  const {
    roles: assignableRoles,
    loading: rolesLoading,
    error: rolesError
  } = useAssignableRoles({ cboId: organizationId });

  // Get the default role (first non-admin role)
  const defaultRole = useMemo(() => {
    if (assignableRoles.length > 0) {
      return assignableRoles[0];
    }
    return null;
  }, [assignableRoles]);

  // Set default role when roles load
  useEffect(() => {
    if (defaultRole && !selectedRoleId) {
      setSelectedRoleId(defaultRole.id);
    }
  }, [defaultRole, selectedRoleId]);

  useEffect(() => {
    loadAssignments();
    loadAvailableResources();
  }, [userId]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const [members, programs] = await Promise.all([
        apiClient.get(`/api/users/${userId}/member-assignments`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }),
        apiClient.get(`/api/users/${userId}/program-assignments`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        })
      ]);

      setMemberAssignments(members.data);
      setProgramAssignments(programs.data);
      // Separate and non-fatal: before the TRUS-732 migration runs, or for a
      // viewer who may not read it, the chips simply show as off.
      try {
        const flags = await apiClient.get(`/api/users/${userId}/case-manager-assignments`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        });
        const next: Record<number, boolean> = {};
        for (const f of Array.isArray(flags.data) ? flags.data : []) next[f.assignment_id] = !!f.is_case_manager;
        setCaseManagerFlags(next);
      } catch {
        setCaseManagerFlags({});
      }
    } catch (err: any) {
      console.error('Failed to load assignments:', err);
      setError(err.response?.data?.detail || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const toggleCaseManager = async (assignment: ProgramAssignment) => {
    const next = !caseManagerFlags[assignment.id];
    try {
      setSaving(true);
      await apiClient.put(
        `/api/users/${userId}/program-assignments/${assignment.id}/case-manager`,
        { is_case_manager: next },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      setCaseManagerFlags((prev) => ({ ...prev, [assignment.id]: next }));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('admin.case_manager_list_failed', 'Could not update the case manager list'));
    } finally {
      setSaving(false);
    }
  };

  const loadAvailableResources = async () => {
    try {
      const [members, programs] = await Promise.all([
        apiClient.get(`/api/members?page=1&page_size=1000`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }),
        apiClient.get(`/api/programs`, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        })
      ]);

      // Members endpoint returns { members: [...], total: N } with first_name/last_name
      const rawMembers = members.data.members || members.data || [];
      setAvailableMembers(rawMembers.map((m: any) => ({
        id: m.id,
        name: m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || `Member #${m.id}`,
        email: m.email || '',
      })));
      setAvailablePrograms(programs.data || []);
    } catch (err: any) {
      console.error('Failed to load resources:', err);
    }
  };

  const assignMember = async () => {
    if (!selectedMemberId) return;

    try {
      setSaving(true);
      await apiClient.post(
        `/api/users/${userId}/member-assignments`,
        {
          member_id: selectedMemberId,
          access_level: memberAccessLevel
        },
        {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }
      );

      await loadAssignments();
      setShowMemberSelector(false);
      setSelectedMemberId(null);
      setMemberAccessLevel('full');
    } catch (err: any) {
      console.error('Failed to assign member:', err);
      alert(`Failed to assign member: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const assignProgram = async () => {
    if (!selectedProgramId || !selectedRoleId) return;

    try {
      setSaving(true);
      await apiClient.post(
        `/api/users/${userId}/program-assignments`,
        {
          program_id: selectedProgramId,
          organization_role_id: selectedRoleId,
          is_program_lead: isProgramLead,
          can_edit_program: isProgramLead, // Program leads can edit program
          can_enroll_members: canEnrollMembers || isProgramLead, // Program leads can enroll
          can_manage_members: canManageMembers // Grants edit on program members (enforced)
        },
        {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }
      );

      await loadAssignments();
      setShowProgramSelector(false);
      setSelectedProgramId(null);
      setSelectedRoleId(defaultRole?.id || null);
      setIsProgramLead(false);
      setCanEnrollMembers(false);
      setCanManageMembers(true);
    } catch (err: any) {
      console.error('Failed to assign program:', err);
      alert(`Failed to assign program: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const removeMemberAssignment = async (memberAssignmentId: number, memberName: string) => {
    if (!confirm(`Remove access to "${memberName}"?`)) return;

    try {
      await apiClient.delete(`/api/users/${userId}/member-assignments/${memberAssignmentId}`, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });
      await loadAssignments();
    } catch (err: any) {
      console.error('Failed to remove assignment:', err);
      alert(`Failed to remove assignment: ${err.response?.data?.detail || err.message}`);
    }
  };

  const toggleCanManageMembers = async (assignment: ProgramAssignment) => {
    const roleId = assignment.organization_role_id ?? assignment.organization_role?.id;
    if (!roleId) {
      alert(t('admin.legacy_assignment_no_role', 'This assignment has no organization role; reassign it to change member edit access.'));
      return;
    }
    try {
      setSaving(true);
      await apiClient.put(
        `/api/users/${userId}/program-assignments/${assignment.id}/role`,
        {
          organization_role_id: roleId,
          can_manage_members: !assignment.can_manage_members
        },
        {
          headers: { 'X-CBO-ID': organizationId.toString() }
        }
      );
      await loadAssignments();
    } catch (err: any) {
      console.error('Failed to update member edit access:', err);
      alert(`Failed to update member edit access: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const removeProgramAssignment = async (programAssignmentId: number, programName: string) => {
    if (!confirm(`Remove access to "${programName}"?`)) return;

    try {
      await apiClient.delete(`/api/users/${userId}/program-assignments/${programAssignmentId}`, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });
      await loadAssignments();
    } catch (err: any) {
      console.error('Failed to remove assignment:', err);
      alert(`Failed to remove assignment: ${err.response?.data?.detail || err.message}`);
    }
  };

  const bulkAssignProgramMembers = async (programId: number, programName: string) => {
    if (!confirm(`Assign ALL members from "${programName}" to ${userName}? This will give them access to every member enrolled in this program.`)) return;

    try {
      setBulkAssigning(true);
      setBulkSuccess(null);

      const response = await apiClient.post(
        `/api/users/${userId}/bulk-assign-program-members`,
        null,
        {
          params: { program_id: programId },
          headers: { 'X-CBO-ID': organizationId.toString() }
        }
      );

      setBulkSuccess(`✓ Successfully assigned ${response.data.assigned_count} members from "${programName}"`);
      await loadAssignments();

      // Clear success message after 5 seconds
      setTimeout(() => setBulkSuccess(null), 5000);
    } catch (err: any) {
      console.error('Failed to bulk assign:', err);
      alert(`Failed to bulk assign members: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkAssigning(false);
    }
  };

  // Filter out already assigned resources
  const unassignedMembers = availableMembers.filter(
    m => !memberAssignments.some(ma => ma.member_id === m.id)
  );

  const unassignedPrograms = availablePrograms.filter(
    p => !programAssignments.some(pa => pa.program_id === p.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="user-assignments bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-5xl w-full max-h-[90vh] overflow-auto">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <User className="w-6 h-6 text-blue-600" />
              {t('admin.user_assignments', 'User Assignments')}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {userName} ({userRole})
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-300">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">{t('admin.error', 'Error')}</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {bulkSuccess && (
        <div className="m-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-green-800 font-medium">{t('admin.bulk_assignment_successful', 'Bulk Assignment Successful')}</p>
            <p className="text-green-600 text-sm mt-1">{bulkSuccess}</p>
          </div>
        </div>
      )}

      {bulkAssigning && (
        <div className="m-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
          <div>
            <p className="text-blue-800 font-medium">{t('admin.bulk_assigning_members', 'Bulk Assigning Members...')}</p>
            <p className="text-blue-600 text-sm mt-1">{t('admin.please_wait_while_we_assign', 'Please wait while we assign all members from the program.')}</p>
          </div>
        </div>
      )}

      <div className="p-6 space-y-8">
        {/* Member Assignments Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Member Assignments ({memberAssignments.length})
            </h3>
            <button
              onClick={() => setShowMemberSelector(!showMemberSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              {t('admin.assign_member', 'Assign Member')}
            </button>
          </div>

          {showMemberSelector && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={selectedMemberId || ''}
                  onChange={(e) => setSelectedMemberId(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('admin.select_member', 'Select member...')}</option>
                  {unassignedMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>

                <select
                  value={memberAccessLevel}
                  onChange={(e) => setMemberAccessLevel(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="full">{t('admin.full_access', 'Full Access')}</option>
                  <option value="view_only">{t('admin.view_only', 'View Only')}</option>
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={assignMember}
                    disabled={!selectedMemberId || saving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    {t('admin.assign', 'Assign')}
                  </button>
                  <button
                    onClick={() => {
                      setShowMemberSelector(false);
                      setSelectedMemberId(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    {t('admin.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {memberAssignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('admin.no_member_assignments_yet', 'No member assignments yet')}
              </div>
            ) : (
              memberAssignments.map(assignment => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{assignment.member_name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {assignment.access_level === 'full' ? 'Full Access' : 'View Only'} •
                      Assigned {fmt.short(assignment.assigned_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeMemberAssignment(assignment.id, assignment.member_name)}
                    className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                    title={t('admin.remove_assignment', 'Remove assignment')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Program Assignments Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-purple-600" />
              Program Assignments ({programAssignments.length})
            </h3>
            <button
              onClick={() => setShowProgramSelector(!showProgramSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              {t('admin.assign_program', 'Assign Program')}
            </button>
          </div>

          {showProgramSelector && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="space-y-4">
                {/* Program and Role Selection Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.program', 'Program')}
                    </label>
                    <select
                      value={selectedProgramId || ''}
                      onChange={(e) => setSelectedProgramId(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">{t('admin.select_program', 'Select program...')}</option>
                      {unassignedPrograms.map(program => (
                        <option key={program.id} value={program.id}>
                          {program.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.organization_role', 'Organization Role')}
                    </label>
                    <select
                      value={selectedRoleId || ''}
                      onChange={(e) => setSelectedRoleId(Number(e.target.value))}
                      disabled={rolesLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                    >
                      {rolesLoading ? (
                        <option value="">{t('admin.loading_roles', 'Loading roles...')}</option>
                      ) : assignableRoles.length === 0 ? (
                        <option value="">{t('admin.no_roles_available', 'No roles available')}</option>
                      ) : (
                        assignableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.display_name} ({role.system_role})
                          </option>
                        ))
                      )}
                    </select>
                    {rolesError && (
                      <p className="text-red-500 text-xs mt-1">{rolesError}</p>
                    )}
                  </div>
                </div>

                {/* Capabilities Row */}
                <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-purple-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isProgramLead}
                      onChange={(e) => {
                        setIsProgramLead(e.target.checked);
                        if (e.target.checked) {
                          setCanEnrollMembers(true);
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <Star className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {t('admin.program_lead', 'Program Lead')}
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={canEnrollMembers}
                      onChange={(e) => setCanEnrollMembers(e.target.checked)}
                      disabled={isProgramLead}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-50"
                    />
                    <Plus className="w-4 h-4 text-green-600" />
                    <span className={`text-sm font-medium ${isProgramLead ? 'text-gray-400' : 'text-gray-700'}`}>
                      {t('admin.can_enroll_members', 'Can Enroll Members')}
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={canManageMembers}
                      onChange={(e) => setCanManageMembers(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <Shield className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {t('admin.can_manage_members', 'Can manage members (edit access)')}
                    </span>
                  </label>
                </div>

                {/* Actions Row */}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setShowProgramSelector(false);
                      setSelectedProgramId(null);
                      setIsProgramLead(false);
                      setCanEnrollMembers(false);
                      setCanManageMembers(true);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    {t('admin.cancel_1', 'Cancel')}
                  </button>
                  <button
                    onClick={assignProgram}
                    disabled={!selectedProgramId || !selectedRoleId || saving}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    {t('admin.assign_1', 'Assign')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {programAssignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('admin.no_program_assignments_yet', 'No program assignments yet')}
              </div>
            ) : (
              programAssignments.map(assignment => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">{assignment.program_name}</p>
                      {assignment.is_program_lead && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <Star className="w-3 h-3" />
                          {t('admin.lead', 'Lead')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {assignment.organization_role?.display_name || assignment.program_role || t('admin.no_role', 'No role')}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-500">
                        {fmt.short(assignment.assigned_at)}
                      </span>
                      {/* Capability badges */}
                      {assignment.can_enroll_members && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                          <Plus className="w-3 h-3" />
                          {t('admin.enroll', 'Enroll')}
                        </span>
                      )}
                      <button
                        onClick={() => toggleCanManageMembers(assignment)}
                        disabled={saving}
                        title={t(
                          'admin.can_manage_members_toggle_hint',
                          'Controls whether this user can edit members in this program (view-only otherwise). Click to toggle.'
                        )}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors disabled:opacity-50 ${
                          assignment.can_manage_members
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        <Shield className="w-3 h-3" />
                        {assignment.can_manage_members
                          ? t('admin.can_edit_members', 'Can edit members')
                          : t('admin.view_only_members', 'View-only members')}
                      </button>
                      {/* TRUS-732: puts this person in the program's case manager dropdown.
                          Display only — it grants no access. */}
                      <button
                        onClick={() => toggleCaseManager(assignment)}
                        disabled={saving}
                        title={t(
                          'admin.case_manager_list_hint',
                          "Whether this person can be picked as this program's case manager. Removing them from the organization takes them off the list."
                        )}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors disabled:opacity-50 ${
                          caseManagerFlags[assignment.id]
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        <UserCheck className="w-3 h-3" />
                        {caseManagerFlags[assignment.id]
                          ? t('admin.on_case_manager_list', 'On case manager list')
                          : t('admin.not_on_case_manager_list', 'Not on case manager list')}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => bulkAssignProgramMembers(assignment.program_id, assignment.program_name)}
                      disabled={bulkAssigning}
                      className="text-blue-600 hover:text-blue-800 px-3 py-1 hover:bg-blue-50 rounded transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t('admin.bulk_assign_all_members_from', 'Bulk assign all members from this program')}
                    >
                      {t('admin.bulk_assign_members', 'Bulk Assign Members')}
                    </button>
                    <button
                      onClick={() => removeProgramAssignment(assignment.id, assignment.program_name)}
                      className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                      title={t('admin.remove_assignment_1', 'Remove assignment')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">{t('admin.about_datalevel_permissions', 'About Data-Level Permissions')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>{t('admin.member_assignments', 'Member Assignments:')}</strong> {t('admin.user_can_only_see_assigned', 'User can only see assigned members')}</li>
                <li><strong>{t('admin.program_assignments', 'Program Assignments:')}</strong> {t('admin.user_sees_all_members_enrolled', 'User sees all members enrolled in assigned programs')}</li>
                <li><strong>{t('admin.organization_roles', 'Organization Roles:')}</strong> {t('admin.roles_define_base_permissions', 'Roles define base permissions from system role mapping')}</li>
                <li><strong>{t('admin.program_lead', 'Program Lead:')}</strong> {t('admin.can_edit_program_and_enroll', 'Can edit program settings and enroll members')}</li>
                <li><strong>{t('admin.admins', 'Admins:')}</strong> org_admin and super_admin always see everything</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
